/**
 * 授权码 + PKCE（RFC 7636）素材与待处理态（附录D §1、附录E 前端篇 §6.1）。
 *
 * <p>code_verifier 只在本地生成、绝不进网络请求以外的任何地方；之所以落到 sessionStorage，
 * 是因为授权码流程要整页跳到中心登录再跳回，页面上下文会丢失。回调页一旦读取即清除，
 * 保证一个 verifier 只能兑换一次。
 */

const VERIFIER_KEY = 'uc.pkce.verifier'
const STATE_KEY = 'uc.pkce.state'
const REDIRECT_KEY = 'uc.pkce.redirect'
const CLIENT_KEY = 'uc.pkce.client'
const TARGET_KEY = 'uc.pkce.target'
/** id_token：RP-Initiated 登出的 id_token_hint 素材（见 api/oauth.ts 的 startLogout）。 */
const ID_TOKEN_KEY = 'uc.oidc.idToken'
/** 主动登出标记：见 router/guards.ts 的注释，没有它"退出后访问首页"会被 SSO 静默登录回去。 */
const LOGGED_OUT_KEY = 'uc.oidc.loggedOut'

export interface PendingAuthorization {
  verifier: string
  challenge: string
  state: string
  /** 发起授权的客户端：uc-me（个人中心）/ uc-admin（管理后台） */
  clientId: string
  /** 授权完成后要回到的前端路径 */
  redirect: string
}

function base64Url(source: ArrayBuffer | Uint8Array): string {
  const bytes = source instanceof Uint8Array ? source : new Uint8Array(source)
  let binary = ''
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte)
  })
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function randomToken(bytes: number): string {
  const buffer = new Uint8Array(bytes)
  crypto.getRandomValues(buffer)
  return base64Url(buffer)
}

/**
 * 由 verifier 派生 S256 challenge（RFC 7636 §4.2）。
 *
 * <p>待处理态只持久化 verifier —— challenge 是它的哈希，随时可重算，
 * 再落一份 sessionStorage 就多一处可能与 verifier 不一致的状态。
 * 因此从 storage 读回的待处理态**必须**用这里重新派生 challenge，不能直接拿着字段用。
 */
export async function deriveChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))
  return base64Url(digest)
}

/** 生成 verifier / challenge(S256) / state 三件套。 */
export async function createPendingAuthorization(
  redirect: string,
  clientId: string,
): Promise<PendingAuthorization> {
  const verifier = randomToken(32)
  return { verifier, challenge: await deriveChallenge(verifier), state: randomToken(16), clientId, redirect }
}

/** 记录发起端（回调失败重试时仍要回到原端），登录成功后由回调页清除。 */
export function saveTarget(target: 'me' | 'admin'): void {
  sessionStorage.setItem(TARGET_KEY, target)
}

export function peekTarget(): 'me' | 'admin' {
  return sessionStorage.getItem(TARGET_KEY) === 'admin' ? 'admin' : 'me'
}

export function clearTarget(): void {
  sessionStorage.removeItem(TARGET_KEY)
}

export function savePending(pending: PendingAuthorization): void {
  sessionStorage.setItem(VERIFIER_KEY, pending.verifier)
  sessionStorage.setItem(STATE_KEY, pending.state)
  sessionStorage.setItem(REDIRECT_KEY, pending.redirect)
  sessionStorage.setItem(CLIENT_KEY, pending.clientId)
}

/** 读取并立即清除（verifier 一次性使用）。 */
export function takePending(): PendingAuthorization | null {
  const pending = peekPending()
  sessionStorage.removeItem(VERIFIER_KEY)
  sessionStorage.removeItem(STATE_KEY)
  sessionStorage.removeItem(REDIRECT_KEY)
  sessionStorage.removeItem(CLIENT_KEY)
  return pending
}

/**
 * 只读不消费：登录页判断"是否处于授权码流程中"用，素材仍留给回调页兑取。
 *
 * <p>返回的 {@code challenge} 恒为空串：storage 里只留 verifier，
 * 需要用它的地方（重新发起授权）自行调 {@link deriveChallenge} 派生，
 * 切勿把这个字段直接拼进 authorize 请求 —— 空的 code_challenge 会被中心判 invalid_request。
 */
export function peekPending(): PendingAuthorization | null {
  const verifier = sessionStorage.getItem(VERIFIER_KEY)
  const state = sessionStorage.getItem(STATE_KEY)
  const redirect = sessionStorage.getItem(REDIRECT_KEY)
  const clientId = sessionStorage.getItem(CLIENT_KEY)
  if (verifier === null || state === null || clientId === null) {
    return null
  }
  return { verifier, challenge: '', state, clientId, redirect: redirect ?? '/me/profile' }
}

/**
 * id_token：只在内存之外多留一份 sessionStorage 副本，为的是 RP-Initiated 登出。
 *
 * <p>它是一次性素材：登出请求一发出就地清除，读不到 / 已过期都按"没有"处理 ——
 * 缺了它 SAS 会以 invalid_request 拒绝 /connect/logout（1.5 起 id_token_hint 为必填），
 * 那时只能退化为"只清本地 + 回登录页"，由服务端的会话销毁兜底。
 */
export function saveIdToken(idToken: string): void {
  sessionStorage.setItem(ID_TOKEN_KEY, idToken)
}

export function peekIdToken(): string | null {
  return sessionStorage.getItem(ID_TOKEN_KEY)
}

export function clearIdToken(): void {
  sessionStorage.removeItem(ID_TOKEN_KEY)
}

/**
 * 标记"本次浏览会话里用户主动退出了"。
 *
 * <p>用途只有一个：退出后再次访问首页时，守卫不再自动发起授权（见 router/guards.ts）。
 * SSO 的本意是"登录一次处处通行"，但用户刚刚按下退出，再被静默送回去就是 bug。
 */
export function markLoggedOut(): void {
  sessionStorage.setItem(LOGGED_OUT_KEY, '1')
}

export function wasLoggedOut(): boolean {
  return sessionStorage.getItem(LOGGED_OUT_KEY) === '1'
}

/** 登录成功（或用户重新走完授权）后清除：此后才是"可以享受 SSO"的状态。 */
export function clearLoggedOut(): void {
  sessionStorage.removeItem(LOGGED_OUT_KEY)
}
